import React, { useMemo } from "react"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  useCarCareServices,
  useCreateCarCareService,
  useEditCarCareService,
} from "hooks/useCarCareService"

const TYPE_OPTIONS = [
  { value: "general_service", label: "General Service" },
  { value: "accessory", label: "Accessory" },
]

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
]

const CarCareServiceAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateCarCareService()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditCarCareService()

  // Parent options — all existing services except the one being edited.
  const { data: servicesData } = useCarCareServices({ all: true })

  const parentOptions = useMemo(() => {
    const list = Array.isArray(servicesData?.data) ? servicesData.data : []
    return list
      .filter(s => String(s.id) !== String(editedData?.id))
      .map(s => ({ value: s.id?.toString(), label: s.name }))
  }, [servicesData, editedData])

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Please enter a service name")
      .max(255, "Name cannot exceed 255 characters"),
    type: Yup.string()
      .oneOf(["general_service", "accessory"])
      .required("Select a type"),
    default_price: Yup.number()
      .min(0, "Price must be 0 or more")
      .nullable(),
    description: Yup.string().max(1000, "Description is too long"),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      type: editedData?.type || "general_service",
      parent_id: editedData?.parent_id?.toString() || "",
      default_price: editedData?.default_price ?? "",
      description: editedData?.description || "",
      is_active:
        editedData?.is_active === undefined
          ? "1"
          : editedData.is_active
          ? "1"
          : "0",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
          parent_id: values.parent_id || null,
          default_price:
            values.default_price === "" ? null : Number(values.default_price),
          is_active: values.is_active === "1",
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        const res = await action
        toast.success(
          res?.message ??
            `Service ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save service.")
      }
    },
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Service"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Car Care Service" : "Add Car Care Service"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {/* Name */}
        <div className="mb-3">
          <Label htmlFor="name">
            Name <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        {/* Type */}
        <FormikCreatableSelect
          name="type"
          label="Type"
          formik={formik}
          options={TYPE_OPTIONS}
          placeholder="Select type"
          required
        />

        {/* Parent */}
        <FormikCreatableSelect
          name="parent_id"
          label="Parent Service"
          formik={formik}
          options={parentOptions}
          placeholder="Select parent (optional)"
        />

        {/* Default Price */}
        <div className="mb-3">
          <Label htmlFor="default_price">Default Sale Price</Label>
          <InputField
            type="number"
            name="default_price"
            id="default_price"
            min="0"
            value={formik.values.default_price}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched.default_price && !!formik.errors.default_price
            }
          />
          {formik.touched.default_price && formik.errors.default_price && (
            <FormFeedback type="invalid">
              {formik.errors.default_price}
            </FormFeedback>
          )}
        </div>

        {/* Status */}
        <FormikCreatableSelect
          name="is_active"
          label="Status"
          formik={formik}
          options={STATUS_OPTIONS}
        />

        {/* Description */}
        <div className="mb-3">
          <Label htmlFor="description">Description</Label>
          <InputField
            type="textarea"
            name="description"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.description && !!formik.errors.description}
          />
          {formik.touched.description && formik.errors.description && (
            <FormFeedback type="invalid">
              {formik.errors.description}
            </FormFeedback>
          )}
        </div>

        <div className="text-end mt-3">
          <Button
            color="success"
            type="submit"
            disabled={editLoading || createLoading}
          >
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default CarCareServiceAddEdit
